import {
  AddressElement,
  Elements,
  ElementsConsumer,
  PaymentElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { toDecimals } from '../../../../util/currency';
import { ShippingAddress } from '../../CheckoutProcess/components/fields/ShippingAddress';
import { Message } from '../../CheckoutProcess/components/Message';
import { PaymentContext } from '../contexts/PaymentContext';

const PAYMENT_OPTION_CC = 'card';
const PAYMENT_OPTION_APPLE = 'apple_pay';
const PAYMENT_OPTION_GOOGLE = 'google_pay';
const PAYMENT_OPTION_AFTERPAY = 'afterpay_clearpay';


const cssSrc = 'https://fonts.googleapis.com/css2?family=Red+Hat+Text:ital,wght@0,300..700;1,300..700&display=swap';
const appearance = {
  theme: 'minimal',
  variables: {
    fontFamily: '"Red Hat Text", "Open Sans", system-ui, sans-serif',
    fontSizeBase: '14px',
    fontSizeSm: '12px',
    fontWeightNormal: '400',
    colorTextPlaceholder: '#585858',
    borderRadius: '8px',
    colorBackground: 'white',
    colorPrimaryText: 'white',
    spacingGridRow: '24px',
    spacingGridColumn: '24px',
  },
  rules: {
    '.Label': {
      marginBottom: '12px',
      fontSize: '14px',
      color: '#000000',
      fontWeight: '500',
    },
    '.Tab, .Input, .Block': {
      border: '1px solid #ABABAB',
      padding: '13px 12px',
    },
  },
};

class StripeCreditCard extends PureComponent {
  stripePromise = null;
  checkedPaymentMethods = false;

  constructor(props) {
    super(props);

    this.stripePromise = loadStripe(props.publicKey, {
      // support automatically using user's email
      // for stripe link
      betas: ['link_default_integration_beta_1'],
    });

    this.state = {
      address: {
        name: this.props.name,
        address: this.props.defaultAddress.address,
        address2: this.props.defaultAddress.address2,
        city: this.props.defaultAddress.city,
        state: this.props.defaultAddress.state,
        country: this.props.defaultAddress.country,
        zip: this.props.defaultAddress.zip,
        phone: this.props.phone,
      },
      useDeliveryAddress: true,
      isDeliveryCompleted: false,
      paymentOption: PAYMENT_OPTION_CC,
      stripePaymentRequest: null,
      loading: false,
      errorCreatingPaymentIntent: false,
      paymentIntentSecret: null,
      paymentIntentId: null,
      processingPayment: false,
      canSubmit: true,
      shouldCancelPaymentIntentOnUnmount: true,
      isPaymentFormCompleted: false,
      isAddressFormCompleted: false,
      elementsLoading: !!this.props.useElementsLoading,
    };
  }

  componentDidMount = async () => {
    if (this.props.paymentIntentSecret !== null && this.props.paymentIntentId !== null) {
      await this.cancelPaymentIntent();
    }
    await this.getPaymentIntent();
    window.addEventListener('beforeunload', this.handleUnmount);
  };

  componentWillUnmount() {
    this.handleUnmount();
    window.removeEventListener('beforeunload', this.handleUnmount);
  }

  handleUnmount = async () => {
    if (this.state.shouldCancelPaymentIntentOnUnmount) {
      await this.cancelPaymentIntent();
    }
  };

  setIsDeliveryAddressCompleted = (isCompleted) => {
    this.setState({ isDeliveryCompleted: isCompleted });
  };

  getPaymentIntent = async () => {
    /**
     * If we have already submitted payment for this reservation, don't create any more payment intents
     */
    if (this.props.stripeSubmittedPayment) {
      return false;
    }

    /**
     * Note that when loading is true, the stripe Elements component does not get rendered.
     * When this gets called after the component mounts, for example when there is an error
     * finalizing the reservation, it unmounts the Elements component and mounts a new one.
     * This is necessary because it is not possible to update the client secret that gets passed
     * to the elements component, so we have to mount a new one in order to use a new payment intent.
     * The unfortunate side effect is that the inputs in the payment element get cleared out when this
     * happens.  This should be a rare occurrence, so it should not have a huge UX impact.
     */
    this.setState({ loading: true });
    let pi = null,
      intentSecret = null,
      intentId = null;
    try {
      const amount = toDecimals({ amount: this.props.amount, currency: this.props.currency });
      pi = await this.props.createStripePaymentIntent(amount, {
        payment_method_types: ['card', 'afterpay_clearpay'],
      });

      intentSecret = pi?.client_secret ? pi.client_secret : null;
      intentId = pi?.intent?.id ? pi.intent.id : null;
    } catch (err) {
      // do nothing
    }

    this.setState({
      loading: false,
      paymentIntentId: intentId,
      paymentIntentSecret: intentSecret,
      errorCreatingPaymentIntent: !pi?.client_secret,
      shouldCancelPaymentIntentOnUnmount: true,
    });

    this.props.setStripePaymentIntentSecret(intentSecret);
    this.props.setStripePaymentIntentId(intentId);
  };

  cancelPaymentIntent = async () => {
    /**
     * No need to cancel any payment intents if payment has already been submitted for this reservation
     */
    if (this.props.stripeSubmittedPayment) {
      return false;
    }

    try {
      await this.props.cancelStripePaymentIntent(this.props.paymentIntentId, this.props.paymentIntentSecret);
    } catch (err) {
      // do nothing
    }
    this.props.setStripePaymentIntentSecret(null);
    this.props.setStripePaymentIntentId(null);
  };

  onStripeAddressChange = (stripeValue, isValid) => {
    this.setState({ isAddressFormCompleted: isValid });

    if (isValid) {
      this.setState({
        address: {
          name: stripeValue.name,
          phone: stripeValue.phone.toString().replace('+', ''),
          address: stripeValue.address.line1,
          address2: stripeValue.address.line2,
          city: stripeValue.address.city,
          state: stripeValue.address.state,
          country: stripeValue.address.country,
          zip: stripeValue.address.postal_code,
        },
      });
    } else {
      this.setState({
        address: {
          phone: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          country: '',
          zip: '',
        },
      });
    }
  };

  getPaymentPayload = () => {
    let payload = {
      email: this.props.email,
      tos_accepted: this.props.tosAccepted,
      captureMethod: this.props.captureMethod,
      emailOptOut: this.props.emailOptOut,
      ...this.state.address,
    };

    if (this.props.createdNewAccount) {
      payload.sendAccountEmail = 1;
    }

    return payload;
  };

  finalizeReservationFromPaymentIntent = (paymentIntentId, secret) => {
    let payload = {
      payment_intent_id: paymentIntentId,
      client_secret: secret,
      ...this.getPaymentPayload(),
    };
    return this.props.finalizeReservationFromPaymentIntent(payload);
  };

  makeReservationWait = (t) => {
    let payload = {
      payment_intent_id: this.state.paymentIntentId ?? null,
      ...this.getPaymentPayload(),
    };

    return this.props.onReservationWait(payload, { onSuccess: () => {}, onError: this.onError, t });
  };

  onError = (errorMessage) => {
    this.setState({ processingPayment: false, canSubmit: true });
    this.props.onPaymentFail([{ detail: errorMessage }]);
  };

  renderActionButton = (onClick) => {
    if (!this.state.canSubmit) {
      return null;
    }

    const handleClick = (t) => {
      if (this.props.tosAccepted) {
        onClick();
      } else {
        this.onError(t.t('checkout__must_agree_term_of_use'));
      }
    };

    const disabled =
      this.props.processingPayment ||
      !this.state.isPaymentFormCompleted ||
      !this.state.isAddressFormCompleted ||
      !this.props.tosAccepted ||
      !this.state.useDeliveryAddress && !this.state.isDeliveryCompleted;

    if (
      (this.state.paymentOption === PAYMENT_OPTION_APPLE || this.state.paymentOption === PAYMENT_OPTION_GOOGLE) &&
      this.state.stripePaymentRequest
    ) {
      if (this.state.paymentOption === PAYMENT_OPTION_APPLE) {
        // Apple pay button
        return (
          <PaymentContext.Consumer>
            {({ TranslationContext }) => (
              <TranslationContext.Consumer>
                {(t) => (
                  <span>
                    <button
                      className="button--primary button--large apple-pay__button"
                      onClick={() => handleClick(t)}
                      disabled={this.state.processingPayment || this.props.loading}
                    >
                      <div className="apple-pay-button-with-text apple-pay-button-black-with-text">
                        <span className="text">{t.t('checkout__buy_with')}</span>
                        <span className="logo"></span>
                      </div>
                    </button>
                  </span>
                )}
              </TranslationContext.Consumer>
            )}
          </PaymentContext.Consumer>
        );
      } else {
        // Google pay button
        return (
          <div>
            {!this.state.stripePaymentRequest ? null : (
              // if google pay is available, stripe JS payment request button
              // will render the google pay button
              <PaymentContext.Consumer>
                {({ TranslationContext }) => (
                  <TranslationContext.Consumer>
                    {(t) => (
                      <PaymentRequestButtonElement
                        options={{
                          paymentRequest: this.state.stripePaymentRequest,
                          style: {
                            paymentRequestButton: {
                              type: 'buy',
                            },
                          },
                        }}
                        onClick={(e) => {
                          // override default behavior
                          e.preventDefault();
                          if (!this.state.processingPayment && !this.props.loading) {
                            handleClick(t);
                          }
                        }}
                      />
                    )}
                  </TranslationContext.Consumer>
                )}
              </PaymentContext.Consumer>
            )}
          </div>
        );
      }
    } else {
      //normal CC
      const isSubmitButtonLoading = this.state.loading || this.state.processingPayment;

      return (
        <PaymentContext.Consumer>
          {({ StepAction }) => <StepAction disabled={disabled} loading={isSubmitButtonLoading} />}
        </PaymentContext.Consumer>
      );
    }
  };

  makePaymentRequest = (stripe) => {
    if (!stripe) {
      return null;
    }

    return stripe.paymentRequest({
      country: 'US', // country code of the Stripe account
      currency: this.props.currency.toLowerCase(), // Stripe is very strict about currency case
      total: {
        label: 'Total',
        amount: this.props.amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });
  };

  /**
   * Check for apple or google pay options
   */
  checkPaymentMethods = (stripe) => {
    if (this.checkedPaymentMethods || !stripe || (!this.supportsApplePay() && !this.supportsGooglePay())) {
      return;
    }

    // not storing in state because this doesn't need to cause a re-render
    this.checkedPaymentMethods = true;

    let paymentRequest = this.makePaymentRequest(stripe);

    paymentRequest.canMakePayment().then((result) => {
      if (result !== null && typeof result === 'object') {
        this.setState({ stripePaymentRequest: paymentRequest });
      }
    });
  };

  getBillingDetails = () => {
    return {
      name: this.state.address.name,
      email: this.props.email,
      phone: this.state.address.phone,
      address: {
        line1: this.state.address.address,
        line2: this.state.address.address2,
        city: this.state.address.city,
        state: this.state.address.state,
        country: this.state.address.country,
        postal_code: this.state.address.zip,
      },
    };
  };

  paymentMethodRequiresRedirect = () => {
    return this.state.paymentOption === PAYMENT_OPTION_AFTERPAY;
  };

  onSubmit = (stripe, elements, t) => {
    return async (e) => {
      if (e) {
        e.preventDefault();
      }

      this.setState({ processingPayment: true });
      this.props.onPaymentInitiated();

      if (!this.props.tosAccepted) {
        this.setState({ processingPayment: false });
        this.onError(t.t('checkout__must_agree_term_of_use'));
        return false;
      }

      const genericErrorMessage = t.t('checkout__error_submit_payment');

      const confirmParams = {
        return_url: window.location.href,
      };

      if (!this.isAppleOrGooglePay()) {
        confirmParams.payment_method_data = {
          billing_details: this.getBillingDetails(),
        };
        if (this.state.paymentOption === PAYMENT_OPTION_AFTERPAY) {
          confirmParams.shipping = this.getBillingDetails();
          delete confirmParams.shipping.email;
        }
      }

      try {
        // for afterpay or any other redirect payment methods
        if (this.state.paymentOption === PAYMENT_OPTION_AFTERPAY) {
          if (!(await this.makeReservationWait(t))) {
            return false;
          }
        }

        if (this.paymentMethodRequiresRedirect()) {
          // don't cancel the payment intent if it requires redirect to authorize
          this.setState({ shouldCancelPaymentIntentOnUnmount: false });
        }

        if (this.state.useDeliveryAddress) {
          //copy billing address data into shipping data
          this.props.setDeliveryAddress({ ...this.state.address });
        }

        if (!(await this.props.validateAndSaveDeliveryAddress(t, false))) {
          this.setState({ processingPayment: false });
          return false;
        }

        const confirmResult = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: 'if_required',
        });

        if (confirmResult.error) {
          this.onError(genericErrorMessage);
          this.setState({ processingPayment: false });
          this.setState({ shouldCancelPaymentIntentOnUnmount: true });
          return false;
        }

        /**
         * At this point, the payment intent has been confirmed and we are sending a request to finalize the reservation,
         * so we should no longer cancel the payment intent on unload.
         */
        this.setState({ shouldCancelPaymentIntentOnUnmount: false });

        const finalizeResult = await this.finalizeReservationFromPaymentIntent(
          confirmResult.paymentIntent.id,
          confirmResult.paymentIntent.client_secret,
        );

        if (!finalizeResult || finalizeResult.errors) {
          await this.cancelPaymentIntent();
          await this.getPaymentIntent();
          const finalizeError =
            Array.isArray(finalizeResult?.errors) && finalizeResult.errors.length
              ? finalizeResult.errors[0]?.detail
              : genericErrorMessage;
          this.onError(finalizeError);
        } else {
          this.props.setStripeSubmittedPayment(true);
          this.props.onPaymentSuccess(finalizeResult.data);
          this.setState({ canSubmit: false });
        }
      } catch (err) {
        this.setState({ shouldCancelPaymentIntentOnUnmount: true });
        this.props.onPaymentFail([err]);
        console.log(err);
      }

      this.setState({ processingPayment: false });
    };
  };

  supportsApplePay = () => {
    return 'apple_pay' in this.props.acceptedCards;
  };

  supportsGooglePay = () => {
    return 'google_pay' in this.props.acceptedCards;
  };

  handlePaymentElementChange = (e) => {
    if (e?.value?.type && e.value.type != this.state.paymentOption) {
      this.setState({ paymentOption: e.value.type });
    }

    this.setState({ isPaymentFormCompleted: e.complete });
  };

  isAppleOrGooglePay = () => {
    return this.state.paymentOption === PAYMENT_OPTION_APPLE || this.state.paymentOption === PAYMENT_OPTION_GOOGLE;
  };

  isShowDeliveryAddressFields() {
    return (
      !this.isAppleOrGooglePay() &&
      !this.paymentMethodRequiresRedirect() &&
      (this.props.deliveryOption.method_name === 'Mail' || this.props.hasDeliverableProducts)
    );
  }

  getStripeLocale() {
    return this.props.locale.split('-')[0].toLowerCase();
  }

  onUseDeliveryAddressChange = () => {
    this.setState({
      useDeliveryAddress: !this.state.useDeliveryAddress,
    });
  };

  onPaymentElementReady = (e) => {
    e.focus();
    this.setState({ elementsLoading: false });
  }

  renderDeliveryAddress() {
    if (!this.isShowDeliveryAddressFields()) {
      return <div></div>;
    }

    return (
      <ShippingAddress
        address={this.props.deliveryAddress}
        onAddressChange={this.props.setDeliveryAddress}
        useShippingAddress={this.state.useDeliveryAddress}
        onUseShippingAddressChange={this.onUseDeliveryAddressChange}
        onCompleteChanged={this.setIsDeliveryAddressCompleted}
        locale={this.props.locale}
        isPhoneRequired={this.props.isPhoneRequired}
        isStripeGateway
      />
    );
  }

  renderAddressFields() {
    if (this.isAppleOrGooglePay()) {
      // if payment option Apple or Google Pay we don't need to show any fields, they will handle that for us
      return <div></div>;
    }

    return (
      <div className="form-card__content form-card__content--stripe-address">
        <AddressElement
          onChange={(e) => this.onStripeAddressChange(e.value, e.complete)}
          options={{
            mode: 'billing',
            fields: {
              phone: 'always',
            },
            validation: {
              phone: {
                required: this.props.isPhoneRequired ? 'always' : 'never',
              },
            },
            defaultValues: {
              name: this.props.name,
              phone: '',
              address: {
                country: this.props.country,
              },
            },
          }}
        />
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div className="loading__spinner">
            <i className="icon-spinner" aria-hidden="true"></i>
          </div>
        </div>
      );
    }

    if (this.state.errorCreatingPaymentIntent) {
      return <Message inlineText error title="Unable to accept payment" />;
    }

    if (!this.state.paymentIntentSecret) {
      return null;
    }

    return (
      <PaymentContext.Consumer>
        {({ TranslationContext, OptInsOuts, Insurance }) => (
          <TranslationContext.Consumer>
            {(t) => (
              <section className="checkout-form__section">
                <Elements
                  stripe={this.stripePromise}
                  options={{ locale: this.getStripeLocale(), clientSecret: this.state.paymentIntentSecret, appearance, cssSrc }}
                >
                  <ElementsConsumer>
                    {({ stripe, elements }) => {
                      this.checkPaymentMethods(stripe);
                      return (
                        <div>
                          <div className="form-card">
                            <div className="form-card__header">
                              <h4 className="form-card__heading">Payment Info</h4>
                            </div>
                            <div className="form-card__content input--stripe-credit-card">
                              <div>
                                <form onSubmit={this.onSubmit(stripe, elements, t)}>
                                  <PaymentElement
                                    options={{
                                      defaultValues: {
                                        billingDetails: {
                                          // must pass email for Stripe Link
                                          email: this.props.email,
                                        },
                                      },
                                      fields: {
                                        billingDetails: this.isAppleOrGooglePay() ? 'auto' : 'never',
                                      },
                                      wallets: {
                                        applePay: this.supportsApplePay() ? 'auto' : 'never',
                                        googlePay: this.supportsGooglePay() ? 'auto' : 'never',
                                      },
                                    }}
                                    onReady={this.onPaymentElementReady}
                                    onChange={this.handlePaymentElementChange}
                                  />
                                  <button style={{ display: 'none' }} id="payment-form-invisible-button" type="submit">
                                    Invisible display button
                                  </button>
                                </form>
                              </div>
                            </div>

                            {this.renderAddressFields()}
                          </div>
                          {!this.state.elementsLoading && (
                            <>
                              {this.renderDeliveryAddress()}
                              {Insurance}
                              {OptInsOuts}
                              {this.renderActionButton(this.onSubmit(stripe, elements, t))}
                            </>
                          )}
                        </div>
                      );
                    }}
                  </ElementsConsumer>
                </Elements>
              </section>
            )}
          </TranslationContext.Consumer>
        )}
      </PaymentContext.Consumer>
    );
  }
}

StripeCreditCard.propTypes = {
  onPaymentInitiated: PropTypes.func.isRequired,
  onPaymentSuccess: PropTypes.func.isRequired,
  onPaymentFail: PropTypes.func.isRequired,
  amount: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string,
  phone: PropTypes.string,
  token: PropTypes.string.isRequired,
  secret: PropTypes.string.isRequired,
  apiUrl: PropTypes.string.isRequired,
  tosAccepted: PropTypes.bool.isRequired,
  acceptedCards: PropTypes.object,
  captureMethod: PropTypes.number,
  isFullyPaid: PropTypes.bool,
  createdNewAccount: PropTypes.bool,
  emailOptOut: PropTypes.bool,
  publicKey: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  stripeSubmittedPayment: PropTypes.bool.isRequired,
  setStripeSubmittedPayment: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  validateAndSaveDeliveryAddress: PropTypes.func.isRequired,
  setDeliveryAddress: PropTypes.func.isRequired,
  deliveryAddress: PropTypes.object.isRequired,
  deliveryOption: PropTypes.object,
  isPhoneRequired: PropTypes.bool,
};

StripeCreditCard.defaultProps = {
  phone: '',
  name: '',
  acceptedCards: {},
  captureMethod: 1, // CAPTURE_ONLINE
};

export default StripeCreditCard;
